import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { ChannelPill } from '@/components/feature/ChannelPill';

export function formatCount(n: number): string {
  if (n >= 100000000) return `${(n / 100000000).toFixed(1)}亿`;
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(n);
}

export function ArticleCard({
  article,
  mode = 'grid',
}: {
  article: Article;
  mode?: 'grid' | 'row';
}) {
  if (mode === 'row') {
    return (
      <article className="group flex gap-4 border-b border-background-200/70 pb-4 last:border-0">
        <Link
          to={`/news/${article.id}`}
          className="relative block h-20 w-28 shrink-0 overflow-hidden rounded-lg sm:h-24 sm:w-40"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title}-热瓜速递`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ChannelPill slug={article.channel} showIcon={false} />
            <span className="text-xs text-foreground-400">{article.date}</span>
          </div>
          <h4 className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 md:text-base">
            <Link to={`/news/${article.id}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h4>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatCount(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {article.read}分钟
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors duration-300 hover:border-primary-200">
      <Link to={`/news/${article.id}`} className="relative block aspect-[4/3] w-full overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title}-热瓜速递`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <ChannelPill slug={article.channel} />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-900">
          <Link to={`/news/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
          {article.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-400">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[11px] font-bold text-secondary-700">
              {article.author.slice(0, 1)}
            </span>
            <span className="truncate">{article.author}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatCount(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              {article.read}分钟
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}